import { useEffect, useRef } from 'react';

import {
  useDidAggregationSucceedForLaunch,
  useIsAggregationSettledForLaunch,
} from '@/api/aggregation/aggregation-settlement-tracker';
import { usePopup } from '@/features/Popup/usePopup';
import {
  isContinueWithoutJackpotsRequested,
  isJackpotModuleRenderable,
  shouldReconcileJackpotMode,
  shouldShowCanadaLaunchAggregationFailure,
  showJackpotLaunchAggregationFailurePopup,
} from '@/features/SWJ/helpers/jackpot-mode-launch';
import { attemptDeliverGlsInitializeUrl } from '@/game-client/gls-pending-initialize-url';
import { runJackpotGlsReconcile } from '@/game-client/jackpot-gls-reconcile-run';
import { useGLS } from '@/game-client/hooks/use-gls';
import { getLastGlsResponseJackpotMode } from '@/game-client/gls-jackpot-mode-tracker';
import { setIsJackpotModeSuppressed } from '@/store/actions';
import useGameUidParam from '@/lostHooks/gameUid/useGameUid';
import resolveLaunchGameUidFromLocation from '@/lostHooks/gameUid/resolveLaunchGameUidFromLocation';
import { isSoftReloadInProgress } from '@/lostHooks/softReload/useSoftReload';
import { useStore } from '@/store/useStore';
import { useShallow } from 'zustand/react/shallow';
import {
  isJackpotReconPrewarmAwaitingContext,
  markJackpotReconPrewarmDeliveryReady,
  resolveLaunchVitalsReconciliation,
} from '@/utilities/logger/datadog/vitals';
import { ddNotifyJackpotReconStart } from '@/utilities/logger/datadog/rumEvents/jackpotRecon';

const useJackpotModeGlsReconcile = () => {
  const { fetchGame } = useGLS();
  const { showPopup } = usePopup();
  const urlGameUid = useGameUidParam();
  const reconcileStartedRef = useRef(false);
  const aggregationFailurePopupShownRef = useRef(false);

  const {
    gameFetchSuccess,
    jackpotProviderUid,
    gameResetRevision,
    clientLoaded,
    jackpotGlsPrewarmContextReceived,
  } = useStore(
    useShallow((s) => ({
      gameFetchSuccess: s.gameStatus.gameFetchSuccess,
      jackpotProviderUid: s.swj.jackpotData?.provider?.uid,
      gameResetRevision: s.gameResetRevision,
      clientLoaded: s.clientStatus.loaded,
      jackpotGlsPrewarmContextReceived:
        s.gameStatus.jackpotGlsPrewarmContextReceived,
    })),
  );

  const launchGameUid = resolveLaunchGameUidFromLocation() ?? urlGameUid;
  const aggregationSettled = useIsAggregationSettledForLaunch(launchGameUid);
  const aggregationSucceeded = useDidAggregationSucceedForLaunch(launchGameUid);

  useEffect(() => {
    reconcileStartedRef.current = false;
    aggregationFailurePopupShownRef.current = false;
  }, [gameResetRevision, launchGameUid]);

  useEffect(() => {
    if (!gameFetchSuccess || !aggregationSettled || isSoftReloadInProgress()) {
      return;
    }

    const glsResponseJackpotMode = getLastGlsResponseJackpotMode();
    const canRenderJackpotModule = isJackpotModuleRenderable();
    const canadaAggregationFailed =
      shouldShowCanadaLaunchAggregationFailure(aggregationSucceeded);

    if (canadaAggregationFailed) {
      if (!aggregationFailurePopupShownRef.current) {
        aggregationFailurePopupShownRef.current = true;
        showJackpotLaunchAggregationFailurePopup(showPopup);
      }
      resolveLaunchVitalsReconciliation(false);
      attemptDeliverGlsInitializeUrl();
      return;
    }

    const shouldReconcile = shouldReconcileJackpotMode({
      glsResponseJackpotMode,
      aggregationSettled,
      aggregationSucceeded,
      canRenderJackpotModule,
    });
    if (!shouldReconcile) {
      resolveLaunchVitalsReconciliation(false);
      attemptDeliverGlsInitializeUrl();
      return;
    }

    if (!clientLoaded) {
      return;
    }

    if (!jackpotGlsPrewarmContextReceived) {
      if (isJackpotReconPrewarmAwaitingContext()) {
        return;
      }
      markJackpotReconPrewarmDeliveryReady();
      attemptDeliverGlsInitializeUrl();
      return;
    }

    if (isContinueWithoutJackpotsRequested()) {
      return;
    }

    if (reconcileStartedRef.current) {
      return;
    }
    reconcileStartedRef.current = true;

    resolveLaunchVitalsReconciliation(true);

    // Suppress via the store, not the URL, so the opt-out survives the
    // reconcile relaunch but is cleared on the next switch / new-game launch.
    setIsJackpotModeSuppressed(true);
    ddNotifyJackpotReconStart(true, {
      glsResponseJackpotMode,
      aggregationSettled,
      canRenderJackpotModule,
    });
    void runJackpotGlsReconcile(fetchGame, urlGameUid, undefined, true);
  }, [
    aggregationSettled,
    clientLoaded,
    gameFetchSuccess,
    jackpotGlsPrewarmContextReceived,
    jackpotProviderUid,
    gameResetRevision,
    fetchGame,
    urlGameUid,
    launchGameUid,
    aggregationSucceeded,
    showPopup,
  ]);
};

export default useJackpotModeGlsReconcile;
